import { useLocation } from "react-router-dom";
import {
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import API_BASE_RELIO from "../../config_Relio";

export default function LitmapResults() {
  const { state } = useLocation();
  const raw = state?.result;

  console.log("Raw result:", raw);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  if (!raw)
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <Typography variant="h5" color="text.secondary">
          No results found
        </Typography>
      </Box>
    );

  const {
    compound,
    outcome,
    mode,
    mode_name,
    genes,
    pathways,
    documents,
    sources,
    images,
    job_id,
    docs_analyzed,
  } = raw;

  // ── genes is an ARRAY of { gene, hits, direction, pathways }
  const geneList = Array.isArray(genes) ? genes : [];

  const geneStats = geneList.map((g) => {
    const dir =
      g.direction === "up"
        ? "UP"
        : g.direction === "down"
          ? "DOWN"
          : g.direction === "neutral"
            ? "NEUTRAL"
            : "MIXED";

    return {
      gene: g.gene,
      hits: g.hits,
      up: g.direction === "up" ? g.hits : 0,
      down: g.direction === "down" ? g.hits : 0,
      neutral: g.direction === "neutral" ? g.hits : 0,
      dir,
    };
  });

  // ── sources is an array of { id, url }
  const sourceList = Array.isArray(sources) ? sources : [];

  // ── documents is a legacy flat array of IDs (may not exist in new shape)
  const documentList = Array.isArray(documents) ? documents : [];

  const getDocLink = (doc) =>
    String(doc).includes("PMC")
      ? `https://www.ncbi.nlm.nih.gov/pmc/articles/${String(doc).replace("PMC", "")}/`
      : `https://pubmed.ncbi.nlm.nih.gov/${doc}/`;

  // ── pathways: object keyed by pathway name → array of gene names
  const pathwaySummary = Object.entries(pathways || {})
    .map(([path, genesInPath]) => {
      const genesArray = Array.isArray(genesInPath) ? genesInPath : [];
      return {
        path: path.length > 60 ? path.substring(0, 60) + "…" : path,
        genes: genesArray,
        count: genesArray.length,
      };
    })
    .sort((a, b) => b.count - a.count);

  const contradictions = geneStats.filter((g) => g.dir === "MIXED");

  // Total document count: prefer docs_analyzed, then sourceList, then documentList
  const totalDocs =
    docs_analyzed ?? sourceList.length ?? documentList.length ?? 0;

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#fafbfc",
        minHeight: "100vh",
        py: 4,
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: "auto", px: 3, width: "100%" }}>
        {/* ── HEADER ── */}
        <Card
          sx={{
            mb: 5,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid rgba(0,0,0,0.05)",
          }}
        >
          <CardContent sx={{ pt: 5, pb: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: isSmall ? "column" : "row",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 3,
                gap: 4,
              }}
            >
              <Typography
                variant={isSmall ? "h4" : "h2"}
                fontWeight={800}
                sx={{ color: "#111827", lineHeight: 1.15, letterSpacing: -0.5 }}
              >
                {compound}
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  px: 3,
                  py: 1.5,
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 2,
                  alignSelf: "flex-start",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#374151", fontWeight: 600, fontSize: "1rem" }}
                >
                  {outcome}
                </Typography>
              </Paper>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 3,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Chip
                label={mode_name || mode}
                size="medium"
                color="primary"
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label={`${totalDocs} documents`}
                size="medium"
                color="secondary"
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label={`${geneList.length} genes`}
                size="medium"
                variant="outlined"
                sx={{
                  color: "#374151",
                  fontWeight: 600,
                  borderColor: "#d1d5db",
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* ── NETWORK GRAPH ── */}
        {images?.graph && images.graph !== "null" && (
          <Card
            sx={{
              mb: 5,
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.05)",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ pt: 3 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ fontWeight: 700, color: "#1a202c" }}
              >
                Network Visualization
              </Typography>
            </CardContent>
            <CardMedia
              component="img"
              image={images.graph_url || images.graph}
              alt="LitMap Graph"
              sx={{
                height: isSmall ? 300 : 500,
                width: "100%",
                objectFit: "contain",
              }}
            />
          </Card>
        )}

        {/* ── GENE TABLE ── */}
        <Card
          sx={{
            mb: 5,
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid rgba(0,0,0,0.05)",
          }}
        >
          <CardContent sx={{ pt: 3 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: 700, color: "#1a202c" }}
            >
              Gene-Target Evidence
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {geneStats.length} genes identified across literature
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <TableContainer
              component={Paper}
              sx={{ borderRadius: 2, overflow: "hidden" }}
            >
              <Table size={isSmall ? "small" : "medium"}>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f8fafc",
                      "& .MuiTableCell-head": {
                        fontWeight: 700,
                        color: "#374151",
                        fontSize: "0.875rem",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      },
                    }}
                  >
                    <TableCell>Gene</TableCell>
                    <TableCell align="center">Hits</TableCell>
                    <TableCell align="center">Direction</TableCell>
                    <TableCell align="center">↑ Up</TableCell>
                    <TableCell align="center">↓ Down</TableCell>
                    <TableCell align="center">○ Neutral</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {geneStats.map((g) => (
                    <TableRow
                      key={g.gene}
                      sx={{
                        "&:hover": { backgroundColor: alpha("#e5e7eb", 0.5) },
                        ...(g.dir === "MIXED" && {
                          backgroundColor: alpha("#fef3c7", 0.3),
                        }),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: "#1f2937" }}>
                        {g.gene}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        {g.hits}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={g.dir}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            minWidth: 70,
                            ...(g.dir === "UP" && {
                              backgroundColor: "#dcfce7",
                              color: "#166534",
                            }),
                            ...(g.dir === "DOWN" && {
                              backgroundColor: "#fee2e2",
                              color: "#991b1b",
                            }),
                            ...(g.dir === "MIXED" && {
                              backgroundColor: "#fef3c7",
                              color: "#92400e",
                            }),
                            ...(g.dir === "NEUTRAL" && {
                              backgroundColor: "#f3f4f6",
                              color: "#6b7280",
                            }),
                          }}
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ color: "#10b981", fontWeight: 500 }}
                      >
                        {g.up}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ color: "#ef4444", fontWeight: 500 }}
                      >
                        {g.down}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#6b7280" }}>
                        {g.neutral}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* ── PATHWAYS + CONTRADICTIONS ── */}
        {(pathwaySummary.length > 0 || contradictions.length > 0) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: isSmall ? "column" : "row",
              gap: 3,
              mb: 5,
            }}
          >
            {/* Pathways */}
            {pathwaySummary.length > 0 && (
              <Card
                sx={{
                  flex: 1,
                  borderRadius: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(0,0,0,0.05)",
                }}
              >
                <CardContent sx={{ pt: 3 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 700, color: "#1a202c" }}
                  >
                    Biological Pathways
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {pathwaySummary.length} pathways mapped
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <TableContainer
                    component={Paper}
                    sx={{ borderRadius: 2, overflow: "hidden" }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 700 }}>
                            Pathway
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>
                            Genes
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>
                            Key Genes
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pathwaySummary
                          .slice(0, 12)
                          .map(({ path, count, genes: pg }) => (
                            <TableRow key={path} hover>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ maxWidth: 300, lineHeight: 1.4 }}
                                >
                                  {path}
                                </Typography>
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontWeight: 600, color: "#059669" }}
                              >
                                {count}
                              </TableCell>
                              <TableCell align="center">
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {pg.slice(0, 3).join(", ")}
                                  {pg.length > 3 && ` +${pg.length - 3}`}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Contradictions */}
            {contradictions.length > 0 && (
              <Card
                sx={{
                  flex: 0.6,
                  borderRadius: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(0,0,0,0.05)",
                  backgroundColor: alpha("#fef3c7", 0.1),
                }}
              >
                <CardContent sx={{ pt: 3 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ color: "#92400e", fontWeight: 700 }}
                  >
                    Conflicting Evidence
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {contradictions.length} genes show mixed signals
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {contradictions.slice(0, 6).map((g) => (
                      <Box
                        key={g.gene}
                        sx={{
                          p: 2.5,
                          border: "1px solid",
                          borderColor: alpha("#f59e0b", 0.3),
                          borderRadius: 2,
                          backgroundColor: "white",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = alpha(
                            "#fef3c7",
                            0.5,
                          );
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "white";
                        }}
                      >
                        <Typography
                          fontWeight={700}
                          sx={{ mb: 0.5, color: "#1f2937" }}
                        >
                          {g.gene}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#92400e", fontWeight: 500 }}
                        >
                          ↑ {g.up} vs ↓ {g.down} ({g.hits} hits)
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {/* ── SOURCE DOCUMENTS ── */}
        {(sourceList.length > 0 || documentList.length > 0) && (
          <Card
            sx={{
              mb: 5,
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <CardContent sx={{ pt: 3 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ fontWeight: 700, color: "#1a202c" }}
              >
                Source Documents
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Primary literature references (
                {sourceList.length || documentList.length} total)
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Box
                sx={{
                  maxHeight: 300,
                  overflow: "auto",
                  backgroundColor: "#f8fafc",
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.05)",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(140px, 1fr))",
                    gap: 1.5,
                  }}
                >
                  {/* New shape: sources array of { id, url } */}
                  {sourceList.length > 0
                    ? sourceList.slice(0, 60).map((src) => (
                        <a
                          key={src.id}
                          href={src.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            textDecoration: "none",
                            fontSize: "0.875rem",
                            fontFamily: "monospace",
                            color: "#3b82f6",
                            fontWeight: 500,
                            padding: "8px 12px",
                            borderRadius: 6,
                            backgroundColor: "white",
                            border: "1px solid rgba(59,130,246,0.1)",
                            transition: "all 0.2s ease",
                            display: "block",
                            textAlign: "center",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#eff6ff";
                            e.currentTarget.style.borderColor = "#3b82f6";
                            e.currentTarget.style.transform =
                              "translateY(-1px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "white";
                            e.currentTarget.style.borderColor =
                              "rgba(59,130,246,0.1)";
                            e.currentTarget.style.transform = "none";
                          }}
                        >
                          {src.id}
                        </a>
                      ))
                    : /* Legacy shape: documents flat array of IDs */
                      documentList.slice(0, 60).map((doc) => (
                        <a
                          key={doc}
                          href={getDocLink(doc)}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            textDecoration: "none",
                            fontSize: "0.875rem",
                            fontFamily: "monospace",
                            color: "#3b82f6",
                            fontWeight: 500,
                            padding: "8px 12px",
                            borderRadius: 6,
                            backgroundColor: "white",
                            border: "1px solid rgba(59,130,246,0.1)",
                            transition: "all 0.2s ease",
                            display: "block",
                            textAlign: "center",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#eff6ff";
                            e.currentTarget.style.borderColor = "#3b82f6";
                            e.currentTarget.style.transform =
                              "translateY(-1px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "white";
                            e.currentTarget.style.borderColor =
                              "rgba(59,130,246,0.1)";
                            e.currentTarget.style.transform = "none";
                          }}
                        >
                          {doc}
                        </a>
                      ))}
                </Box>
                {(sourceList.length > 60 || documentList.length > 60) && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2, textAlign: "center", fontStyle: "italic" }}
                  >
                    … and {(sourceList.length || documentList.length) - 60} more
                    documents
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* ── ACTIONS ── */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid rgba(0,0,0,0.05)",
          }}
        >
          <CardContent
            sx={{ textAlign: "center", pt: 4, pb: 4, backgroundColor: "white" }}
          >
            <Button
              variant="contained"
              size="large"
              href={`${API_BASE_RELIO}/results/${job_id}_report.txt`}
              target="_blank"
              sx={{
                mr: 3,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontSize: "1.1rem",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(59,130,246,0.4)",
                backgroundColor: "#3b82f6",
                "&:hover": {
                  backgroundColor: "#2563eb",
                  boxShadow: "0 6px 20px rgba(59,130,246,0.5)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              📥 Download Full Report
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => window.history.back()}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontSize: "1.1rem",
                fontWeight: 600,
                borderColor: "#d1d5db",
                color: "#374151",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f9fafb",
                  transform: "translateY(-1px)",
                },
              }}
            >
              ← New Analysis
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
